import { MetaFunction } from '@remix-run/node';
import { useLoaderData, useSubmit } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatedForm } from 'remix-validated-form';
import { FacetFilterTracker } from '~/components/facet-filter/facet-filter-tracker';
import { CategoryFilterBar } from '~/components/collections/CategoryFilterBar';

import { FacetValueResult } from '~/generated/graphql';
import { InfiniteProductGrid } from '~/components/products/InfiniteProductGrid';
import { filteredSearchLoaderFromPagination } from '~/utils/filtered-search-loader';
import { paginationValidationSchema } from '~/utils/pagination';

// Meta function for SEO
export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  const term = data?.term;
  const totalItems = data?.result?.totalItems ?? 0;

  const title = term
    ? `Search: "${term}" - RedTab`
    : 'All Products - RedTab';

  const description = term
    ? `Found ${totalItems} results for "${term}" on RedTab. Browse our selection of quality products.`
    : 'Browse all products on RedTab. Find the best deals on quality items.';

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { name: 'robots', content: term ? 'noindex, follow' : 'index, follow' },
  ];
};

const paginationLimitMinimumDefault = 24;
const allowedPaginationLimits = new Set<number>([
  paginationLimitMinimumDefault,
  48,
  72,
]);
const validator = withZod(paginationValidationSchema(allowedPaginationLimits));

export const { filteredSearchLoader: loader } =
  filteredSearchLoaderFromPagination(
    allowedPaginationLimits,
    paginationLimitMinimumDefault,
  );

export default function Search() {
  const loaderData = useLoaderData<Awaited<typeof loader>>();
  const { result, resultWithoutFacetValueFilters, term, facetValueIds } =
    loaderData;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const facetValuesTracker = useRef(new FacetFilterTracker());
  facetValuesTracker.current.update(
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
  );
  const submit = useSubmit();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 md:px-6 lg:px-8 xl:px-8 py-8">
        <div className="flex justify-between items-center">
          <h2 className="text-xl sm:text-3xl md:text-5xl font-light tracking-tight text-gray-900 my-4 sm:my-8">
            {term
              ? `${t('common.resultsFor')} "${term}"`
              : t('common.allResults')}
          </h2>


        </div>

        <CategoryFilterBar
          orderedCount={5}
          facetValues={(resultWithoutFacetValueFilters || result)?.facetValues as FacetValueResult[] | undefined}
          activeFacetValueIds={facetValueIds}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
        <ValidatedForm
          validator={validator}
          method="get"
          onChange={(e) => submit(e.currentTarget, { preventScrollReset: true })}
        >
          <InfiniteProductGrid
            mobileFiltersOpen={mobileFiltersOpen}
            setMobileFiltersOpen={setMobileFiltersOpen}
            hideFilters={true}
            viewMode={viewMode}
            {...loaderData}
          />
        </ValidatedForm>
      </div>
    </div>
  );
}
